import { forwardRef, InputHTMLAttributes, TextareaHTMLAttributes, useId, type ReactNode } from 'react';
import { LucideIcon } from 'lucide-react';
import clsx from 'clsx';
import { FieldLabelWithHelp } from './Tooltip';

interface BaseInputProps {
  label?: string;
  error?: string;
  icon?: LucideIcon;
  /**
   * WAYP-561 pass 18 (2026-04-28): visual-only required indicator. When
   * `true`, the label renders a red `*` after the label text WITHOUT
   * setting the native HTML `required` attribute on the underlying
   * `<input>`. This lets a form opt out of browser-native HTML5
   * validation (via `<form noValidate>`) while still showing the user
   * which fields are required — Zod / server validation stays
   * authoritative. The legacy `required` prop continues to work and
   * also drives the visual marker, for back-compat with callers that
   * intentionally want browser validation.
   */
  requiredMarker?: boolean;
  /** Optional contextual help rendered as an accessible tooltip beside the label. */
  helpText?: ReactNode;
  /**
   * Optional trailing adornment rendered inside the input on the right
   * (e.g., `%`, `mi`, currency code). Visual-only — does not become
   * part of the input's value. Right-pads the input so the typed text
   * doesn't overlap the suffix.
   */
  suffix?: ReactNode;
}

interface InputComponentProps extends InputHTMLAttributes<HTMLInputElement>, BaseInputProps {
  type?: 'text' | 'email' | 'password' | 'number' | 'date' | 'tel' | 'url';
}

interface TextareaComponentProps extends TextareaHTMLAttributes<HTMLTextAreaElement>, BaseInputProps {}

export const Input = forwardRef<HTMLInputElement, InputComponentProps>(
  (
    {
      label,
      error,
      icon: Icon,
      className,
      type = 'text',
      disabled = false,
      requiredMarker,
      helpText,
      suffix,
      id: idProp,
      ...props
    },
    ref
  ) => {
    const showMarker = requiredMarker || props.required;
    // WAYP-534: auto-generate id when caller omits one so the <label htmlFor>
    // pairing resolves. Fixes WCAG 1.3.1 (label-control association) and
    // makes Playwright's getByRole('textbox', { name }) + RTL's
    // getByLabelText() resolve the input by its accessible name.
    const autoId = useId();
    const resolvedId = idProp || autoId;
    return (
      <div className="group/help-field w-full">
        {label && (
          <FieldLabelWithHelp
            label={label}
            htmlFor={resolvedId}
            required={Boolean(showMarker)}
            helpText={helpText}
          />
        )}
        <div className="relative">
          {Icon && (
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Icon className="h-4 w-4 text-gray-500" />
            </div>
          )}
          <input
            ref={ref}
            id={resolvedId}
            type={type}
            disabled={disabled}
            className={clsx(
              'w-full rounded border border-[#ced4da] bg-white text-[13.2px] text-[#212529] placeholder-[#adb5bd] transition-all duration-150',
              'dark:border-white/15 dark:bg-[#1A1A1A] dark:text-white dark:placeholder-gray-500',
              'focus:border-[#468AD4] focus:outline-none focus:ring-1 focus:ring-[#468AD4] focus:ring-offset-0',
              'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
              'dark:disabled:bg-[#141414] dark:disabled:text-gray-500',
              error && 'border-red-600 focus:ring-red-600',
              Icon && 'pl-10',
              suffix && 'pr-8',
              className
            )}
            {...props}
          />
          {suffix && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-[13.2px] text-gray-500">
              {suffix}
            </div>
          )}
        </div>
        {error && (
          <p role="alert" className="mt-1 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaComponentProps>(
  (
    {
      label,
      error,
      icon: Icon,
      className,
      disabled = false,
      rows = 4,
      requiredMarker,
      helpText,
      id: idProp,
      ...props
    },
    ref
  ) => {
    const showMarker = requiredMarker || props.required;
    // WAYP-534: see Input above — same htmlFor/id pairing for WCAG 1.3.1.
    const autoId = useId();
    const resolvedId = idProp || autoId;
    return (
      <div className="group/help-field w-full">
        {label && (
          <FieldLabelWithHelp
            label={label}
            htmlFor={resolvedId}
            required={Boolean(showMarker)}
            helpText={helpText}
          />
        )}
        <div className="relative">
          {Icon && (
            <div className="pointer-events-none absolute top-3 left-0 flex items-start pl-3">
              <Icon className="h-4 w-4 text-gray-500" />
            </div>
          )}
          <textarea
            ref={ref}
            id={resolvedId}
            disabled={disabled}
            rows={rows}
            className={clsx(
              // 2026-05-13 — added px-3 py-2 (mirrors Input default; was
              // missing here too, made textareas read as "naked" boxes).
              'w-full rounded border border-[#ced4da] bg-white px-3 py-2 text-[13.2px] text-[#212529] placeholder-[#adb5bd] transition-all duration-150 resize-none',
              'focus:border-[#468AD4] focus:outline-none focus:ring-1 focus:ring-[#468AD4] focus:ring-offset-0',
              'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
              error && 'border-red-600 focus:ring-red-600',
              Icon && 'pl-10',
              className
            )}
            {...props}
          />
        </div>
        {error && (
          <p role="alert" className="mt-1 text-sm text-red-600">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';
